import React from "react";
import { Link } from "react-router-dom";
import { Scale } from "lucide-react";

const Section = ({ id, title, children }) => (
  <section className="mb-10" data-testid={`legal-${id}`}>
    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// {id.toUpperCase()}</div>
    <h2 className="font-display text-2xl sm:text-3xl text-[#E4DFD3] mb-3">{title}</h2>
    <div className="text-sm text-[#A39E93] leading-relaxed space-y-3">{children}</div>
  </section>
);

const Legal = () => (
  <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="legal-page">
    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-3">// LEGAL NOTICE</div>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#E4DFD3] mb-5">
      The fine print, kept short.
    </h1>
    <p className="text-base text-[#A39E93] leading-relaxed mb-12 max-w-2xl">
      The Archive is editorial commentary on contested public material. This page
      explains the legal posture of the site in plain language.
    </p>

    <Section id="nature" title="What this site is">
      <p>
        The Archive is an independent editorial project that aggregates, summarises,
        and links to publicly available material — court filings, government
        records, reporting from established outlets, and primary documents. It is
        not a newsroom, a court, or an investigative authority.
      </p>
      <p>
        Material published here is opinion and commentary based on public records.
        It is not legal advice, professional advice, or a finding of fact.
      </p>
    </Section>

    <Section id="no-accusation" title="Inclusion is not an accusation">
      <p>
        Appearance of a name, organisation, or document in any record on The
        Archive — including The Epstein Files — reflects only a documented public
        connection. It does not imply criminal conduct, wrongdoing, or guilt by
        association.
      </p>
      <p>
        Every record is labeled with the evidence behind it. Read the source before
        drawing a conclusion.
      </p>
    </Section>

    <Section id="sources" title="Sources & copyright">
      <p>
        Underlying documents and reporting belong to their respective publishers,
        courts, and authors. Where we quote or excerpt, we do so under fair use
        for the purpose of commentary, criticism, and public interest reporting.
      </p>
      <p>
        Editorial summaries on The Archive may not be reproduced without
        attribution and a link back to the original page on this site.
      </p>
    </Section>

    <Section id="corrections" title="Corrections & disputes">
      <p>
        If you believe a record is inaccurate, incomplete, or relies on a source
        we have misread, use the submission form to file a correction or dispute.
        Every claim on the site is anchored to a public source — point us at the
        document that contradicts it and an editor will review.
      </p>
      <p>
        Verified corrections are applied to the record. Where a source is shown
        to be wrong or withdrawn, the record is updated or removed.
      </p>
      <div className="pt-2 flex flex-wrap gap-3">
        <Link
          to="/submit?mode=correction"
          data-testid="legal-submit-btn"
          className="inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          File a correction
        </Link>
        <Link
          to="/editorial-standards"
          data-testid="legal-standards-btn"
          className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          Editorial standards
        </Link>
      </div>
    </Section>

    <Section id="liability" title="No warranty">
      <p>
        Material on The Archive is provided "as is" without warranty of any kind.
        We make a good-faith effort to source every claim, but we make no
        guarantee of completeness, accuracy, or current relevance. Always verify
        material against the linked source before relying on it.
      </p>
      <p>
        The Archive accepts no liability for decisions taken on the basis of
        material published here.
      </p>
    </Section>

    <Section id="contact" title="Contact">
      <p>
        For corrections, disputes, source removals, and editorial questions, use
        the in-app submission form. It routes directly into editorial review.
      </p>
    </Section>

    <div className="mt-12 border border-[#2D2A26] bg-[#0F0F0F] p-5 flex items-center gap-4">
      <Scale size={18} className="text-[#D92D20] shrink-0" />
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
        The Archive · Last updated {new Date().toLocaleDateString(undefined, { year: "numeric", month: "short" })}
      </p>
    </div>
  </div>
);

export default Legal;
